import { useLocation, useNavigate } from 'react-router-dom';

import { Avatar, Box, Divider, NavLink, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconActivityHeartbeat,
  IconAlertHexagon,
  IconAlertTriangle,
  IconArrowLeft,
  IconBattery2,
  IconCalendar,
  IconChartBar,
  IconChartPie,
  IconChecklist,
  IconCurrencyDollar,
  IconFileText,
  IconLayoutDashboard,
  IconSettings,
  IconSignature,
  IconUsers
} from '@tabler/icons-react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAnyFormDirty } from '@/store/slices/form/transmissionReportFormSlice';
import { setActiveColorCategory } from '@/store/slices/transmissionReports/transmissionReportsSlice';
import { useBoxInclinicFeatureFlag } from '@/hooks/useBoxInclinicFeatureFlag';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useTransmissionReportFeature } from '@/hooks/useTransmissionReportFeature';
import routes from '@/routes/config';
import { RhythmLogo } from '@/assets/icons';

import { canViewSchedule } from '@/pages/physician/SchedulePage/scheduleList';
import { ReportColor } from '@/types/transmissionReport';
import ConfirmationModal from '@/ui/ConfirmationModal';

import { OmniChannelWidget } from './OmniChannelWidget';

const ICON_STROKE = 1.5;
const ICON_SIZE = 20;

const ANALYTICS_ITEMS = [
  {
    label: 'Account Overview',
    icon: <IconChartPie size={ICON_SIZE} stroke={ICON_STROKE} />,
    path: routes.physician.analytics.accountOverview
  },
  {
    label: 'Alerts',
    icon: <IconAlertTriangle size={ICON_SIZE} stroke={ICON_STROKE} />,
    path: routes.physician.analytics.alerts
  },
  {
    label: 'Billing',
    icon: <IconCurrencyDollar size={ICON_SIZE} stroke={ICON_STROKE} />,
    path: routes.physician.analytics.billing
  },
  {
    label: 'Signature Status',
    icon: <IconSignature size={ICON_SIZE} stroke={ICON_STROKE} />,
    path: routes.physician.analytics.signatureStatus
  },
  {
    label: 'Battery Life',
    icon: <IconBattery2 size={ICON_SIZE} stroke={ICON_STROKE} />,
    path: routes.physician.analytics.batteryLife
  },
  {
    label: 'Device Recalls',
    icon: <IconAlertHexagon size={ICON_SIZE} stroke={ICON_STROKE} />,
    path: routes.physician.analytics.deviceRecalls
  }
];

interface SideNavProps {
  collapsed: boolean;
}

export function SideNav({ collapsed }: SideNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isBoxInclinicFeatureEnabled } = useBoxInclinicFeatureFlag();
  const { getDefaultReportRoute: getTransmissionReportRoute } = useTransmissionReportFeature();
  const { user } = useAppSelector((state) => state.auth);
  const isAnyFormDirty = useAppSelector(selectIsAnyFormDirty);
  const { requestNavigation, confirmNavigation, cancelNavigation, isModalOpen } =
    useSafeNavigate(isAnyFormDirty);

  const isInternalUser = user?.role?.includes('internal') ?? false;

  const inAnalytics = location.pathname.startsWith(routes.physician.analytics.root);

  const isActive = (path: string) => location.pathname.startsWith(path.split('/*')[0]);

  const isOnTransmissionReportPage = location.pathname.includes(
    routes.physician.defaultTransmissionReport
  );

  const handleNavigate = (path: string, isTransmission = false) => {
    if (isTransmission) {
      dispatch(setActiveColorCategory({ color: ReportColor.Red }));
    }
    if (isOnTransmissionReportPage) {
      requestNavigation(path);
    } else {
      navigate(path);
    }
  };

  const initials = user ? `${user.given?.[0] ?? ''}${user.family?.[0] ?? ''}`.toUpperCase() : 'U';

  const navLinkStyles = (active: boolean) => ({
    root: {
      borderRadius: 8,
      color: active ? '#ffffff' : 'rgba(255,255,255,0.65)',
      backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: '#ffffff' },
      padding: collapsed ? '10px 12px' : '10px 14px',
      fontWeight: 500,
      fontSize: 14
    }
  });

  const renderNavLink = (
    label: string,
    icon: React.ReactNode,
    path: string,
    isTransmission = false
  ) => {
    const active = isActive(path);
    return (
      <Tooltip key={label} label={label} position="right" disabled={!collapsed} withArrow>
        <NavLink
          label={collapsed ? null : label}
          leftSection={icon}
          active={active}
          onClick={() => handleNavigate(path, isTransmission)}
          styles={navLinkStyles(active)}
        />
      </Tooltip>
    );
  };

  const navItems = [
    {
      label: 'Dashboard',
      icon: <IconLayoutDashboard size={ICON_SIZE} stroke={ICON_STROKE} />,
      path: routes.physician.dashboard
    },
    {
      label: 'Patients',
      icon: <IconUsers size={ICON_SIZE} stroke={ICON_STROKE} />,
      path: routes.physician.patientsList
    },
    ...(canViewSchedule(user?.role)
      ? [
          {
            label: 'Schedule',
            icon: <IconCalendar size={ICON_SIZE} stroke={ICON_STROKE} />,
            path: routes.physician.schedule
          }
        ]
      : []),
    {
      label: 'Transmissions',
      icon: <IconActivityHeartbeat size={ICON_SIZE} stroke={ICON_STROKE} />,
      path: getTransmissionReportRoute(),
      isTransmission: true
    },
    ...(isInternalUser
      ? [
          {
            label: 'Tasks',
            icon: <IconChecklist size={ICON_SIZE} stroke={ICON_STROKE} />,
            path: routes.physician.tasks
          }
        ]
      : []),
    {
      label: 'Analytics',
      icon: <IconChartBar size={ICON_SIZE} stroke={ICON_STROKE} />,
      path: routes.physician.analytics.accountOverview
    },
    ...(isBoxInclinicFeatureEnabled
      ? [
          {
            label: 'In Clinics',
            icon: <IconFileText size={ICON_SIZE} stroke={ICON_STROKE} />,
            path: routes.physician.inClinic
          }
        ]
      : [])
  ];

  return (
    <>
      <Stack mih="100%" justify="space-between" gap={0} py="md">
        {/* Logo */}
        <Box px={collapsed ? 10 : 16} mb="md">
          <RhythmLogo iconOnly={collapsed} height={collapsed ? 30 : 26} />
        </Box>

        {/* Main nav — swaps to Analytics sub-nav when in Analytics */}
        <Stack gap={2} px={8} style={{ flex: 1 }}>
          {inAnalytics ? (
            <>
              {renderNavLink(
                'Back',
                <IconArrowLeft size={ICON_SIZE} stroke={ICON_STROKE} />,
                routes.physician.dashboard
              )}
              <Divider color="rgba(255,255,255,0.1)" my={6} />
              {ANALYTICS_ITEMS.map((item) => renderNavLink(item.label, item.icon, item.path))}
            </>
          ) : (
            navItems.map((item) =>
              renderNavLink(item.label, item.icon, item.path, item.isTransmission)
            )
          )}
        </Stack>

        {/* Bottom: user profile opens settings */}
        <Stack gap={2} px={8} pb={4}>
          <Divider color="rgba(255,255,255,0.1)" mb={4} />

          {isInternalUser && <OmniChannelWidget collapsed={collapsed} />}

          <Tooltip label="Settings" position="right" disabled={!collapsed} withArrow>
            <Box
              mt={8}
              px={collapsed ? 4 : 8}
              py={8}
              onClick={() => handleNavigate(routes.user.settings)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                borderRadius: 8,
                cursor: 'pointer',
                background: isActive(routes.user.settings)
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(255,255,255,0.06)'
              }}>
              <Avatar size={32} radius="xl" color="orange">
                {initials}
              </Avatar>
              {!collapsed && (
                <>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" c="white" fw={500} truncate>
                      {user?.given} {user?.family}
                    </Text>
                    <Text size="xs" c="rgba(255,255,255,0.5)" truncate>
                      {user?.role?.split(':').slice(-1)[0] ?? 'Clinician'}
                    </Text>
                  </Box>
                  <IconSettings size={16} stroke={ICON_STROKE} color="rgba(255,255,255,0.55)" />
                </>
              )}
            </Box>
          </Tooltip>
        </Stack>
      </Stack>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={cancelNavigation}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to leave this page?"
        onConfirm={confirmNavigation}
        isLoading={false}
      />
    </>
  );
}
